import { Component } from '@theme/component';
import { VisibilityThresholdState } from './visibility-threshold-state.js';

const AUDIO_FADE_OUT_DURATION_MS = 160;
const AUDIO_FADE_OUT_STEPS = 4;
const PLAYBACK_VISIBILITY_THRESHOLD = 0.4;
const PRELOAD_VISIBILITY_THRESHOLD = 0.01;
const PRELOAD_ROOT_MARGIN = '0px';

/**
 * @param {number} ms
 */
function wait(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 * @property {HTMLButtonElement} [muteButton] - Optional mute toggle button.
 * @property {HTMLElement} [unmuteIcon] - Optional icon shown while muted.
 * @property {HTMLElement} [muteIcon] - Optional icon shown while unmuted.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  /** @type {VisibilityThresholdState | null} */
  #visibilityState = null;

  #visibilityRatio = 0;

  #sourcesLoaded = false;

  #prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /** @type {(() => void) | null} */
  #onMediaReady = null;

  /** @type {(() => void) | null} */
  #onVisibilityChange = null;

  #muteRequestId = 0;

  #isUnmutingOthers = false;

  connectedCallback() {
    super.connectedCallback();

    const { videoElement } = this.refs;

    this.#onMediaReady = () => this.#syncPlayback();
    videoElement.addEventListener('loadeddata', this.#onMediaReady);
    videoElement.addEventListener('canplay', this.#onMediaReady);

    this.#syncMuteButtonState();

    if (this.#prefersReducedMotion) {
      this.#disableMuteToggle();
      return;
    }

    this.#visibilityState = new VisibilityThresholdState({
      target: this,
      rootMargin: PRELOAD_ROOT_MARGIN,
      thresholds: [0, PRELOAD_VISIBILITY_THRESHOLD, PLAYBACK_VISIBILITY_THRESHOLD, 1],
      onChange: ({ ratio }) => {
        this.#visibilityRatio = ratio;

        if (ratio >= PRELOAD_VISIBILITY_THRESHOLD) {
          this.#ensureSourcesLoaded();
        }

        this.#syncPlayback();
      },
    });

    this.#visibilityState.connect();

    this.#onVisibilityChange = () => this.#syncPlayback();
    document.addEventListener('visibilitychange', this.#onVisibilityChange);
  }

  disconnectedCallback() {
    const { videoElement } = this.refs;

    this.#visibilityState?.disconnect();
    this.#visibilityState = null;

    if (this.#onMediaReady) {
      videoElement.removeEventListener('loadeddata', this.#onMediaReady);
      videoElement.removeEventListener('canplay', this.#onMediaReady);
      this.#onMediaReady = null;
    }

    if (this.#onVisibilityChange) {
      document.removeEventListener('visibilitychange', this.#onVisibilityChange);
      this.#onVisibilityChange = null;
    }

    super.disconnectedCallback();
  }

  /**
   * @param {MouseEvent} event
   */
  async toggleMute(event) {
    event.stopPropagation();

    const { videoElement } = this.refs;
    const nextMuted = !videoElement.muted;

    if (!nextMuted) {
      this.#cancelPendingMute();
      videoElement.muted = false;
      videoElement.volume = 1;
      videoElement.play().catch(() => {});
      this.#syncMuteButtonState();
      void this.#muteOtherBackgroundVideos();
      return;
    }

    await this.#muteWithFade();
  }

  async mute() {
    await this.#muteWithFade();
  }

  async #muteWithFade() {
    const { videoElement } = this.refs;
    const requestId = ++this.#muteRequestId;

    if (videoElement.muted) {
      this.#syncMuteButtonState();
      return;
    }

    if (this.#prefersReducedMotion) {
      videoElement.muted = true;
      videoElement.volume = 1;
      this.#syncMuteButtonState();
      return;
    }

    const fadeStepDuration = AUDIO_FADE_OUT_DURATION_MS / AUDIO_FADE_OUT_STEPS;
    const initialVolume = Math.max(0, Math.min(1, videoElement.volume || 1));

    for (let step = 1; step <= AUDIO_FADE_OUT_STEPS; step += 1) {
      if (requestId !== this.#muteRequestId) return;
      if (videoElement.muted) break;
      videoElement.volume = initialVolume * ((AUDIO_FADE_OUT_STEPS - step) / AUDIO_FADE_OUT_STEPS);
      await wait(fadeStepDuration);
    }

    if (requestId !== this.#muteRequestId) return;

    videoElement.muted = true;
    videoElement.volume = 1;
    this.#syncMuteButtonState();
  }

  #cancelPendingMute() {
    this.#muteRequestId += 1;
  }

  #syncPlayback() {
    const { videoElement } = this.refs;
    const shouldPlay = this.#visibilityRatio >= PLAYBACK_VISIBILITY_THRESHOLD && document.visibilityState === 'visible';

    if (shouldPlay) {
      this.#ensureSourcesLoaded();
      this.#cancelPendingMute();
      if (!videoElement.muted) {
        videoElement.volume = 1;
      }
      videoElement.play().catch(() => {});
    } else {
      videoElement.pause();
      if (this.#sourcesLoaded) {
        void this.#muteWithFade();
      }
    }
  }

  #ensureSourcesLoaded() {
    if (this.#sourcesLoaded) return;

    const { videoSources, videoElement } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;
      if (videoSource) {
        source.setAttribute('src', videoSource);
      }
    }

    videoElement.load();
    this.#sourcesLoaded = true;

    // Ensure autoplay retries after lazy-loaded sources become playable.
    queueMicrotask(() => this.#syncPlayback());
  }

  async #muteOtherBackgroundVideos() {
    if (this.#isUnmutingOthers) return;

    this.#isUnmutingOthers = true;
    const backgroundVideos = document.querySelectorAll('video-background-component');
    const mutePromises = [];

    for (const backgroundVideo of backgroundVideos) {
      if (backgroundVideo === this) continue;
      if (!(backgroundVideo instanceof VideoBackgroundComponent)) continue;

      mutePromises.push(backgroundVideo.mute());
    }

    try {
      if (mutePromises.length > 0) {
        await Promise.allSettled(mutePromises);
      }
    } finally {
      this.#isUnmutingOthers = false;
    }
  }

  #disableMuteToggle() {
    const { muteButton } = this.refs;
    if (!(muteButton instanceof HTMLButtonElement)) return;

    muteButton.hidden = true;
    muteButton.disabled = true;
    muteButton.setAttribute('aria-hidden', 'true');
  }

  #syncMuteButtonState() {
    const { videoElement, muteButton, unmuteIcon, muteIcon } = this.refs;
    if (!(muteButton instanceof HTMLButtonElement)) return;

    const isMuted = videoElement.muted;
    const mutedLabel = this.dataset.mutedLabel || 'Unmute video';
    const unmutedLabel = this.dataset.unmutedLabel || 'Mute video';
    const label = isMuted ? mutedLabel : unmutedLabel;

    muteButton.setAttribute('aria-pressed', String(!isMuted));
    muteButton.setAttribute('aria-label', label);

    if (unmuteIcon instanceof HTMLElement) {
      unmuteIcon.hidden = !isMuted;
    }

    if (muteIcon instanceof HTMLElement) {
      muteIcon.hidden = isMuted;
    }
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
